import { payerTypeConstants } from "constants/order";
import { usePayerInformation } from "hooks/usePayerInformation";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { useGetAccountByIdQuery } from "store/queries/account";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IFacilityRules, INonFacilityRules } from "types/Order/Order";

import { PrimaryInsuranceSection } from "./components/PrimaryInsuranceSection";
import { SecondaryInsuranceSection } from "./components/SecondaryInsuranceSection";

const StyledCard = styled(Card)<{ ordertypefield: string }>`
  margin-bottom: ${sizer(4)};
  display: ${({ ordertypefield }) =>
    ordertypefield === "GFE" ? "flex" : "none"};
  flex-direction: column;
  gap: ${sizer(2)};
  .p-card-body {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
  .p-card-title {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
`;

export function PatientBenefitsSection({
  redo = false,
  draft = false,
  copy = false,
  account,
  costApplicationRules,
  secondaryCostApplicationRules,
}: {
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  account: IAccount;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
  secondaryCostApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
}) {
  const {
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();
  const query = useQuery();
  const accountId = query.get("accountId") || "";
  const [showSecondaryInsuranceButton, setShowSecondaryInsuranceButton] =
    useState(true);
  const { selectedDraftOrder: draftOrder } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { selectedOrderToRedo: orderToRedo } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { selectedOrderToCopy: orderToCopy } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { payersNameInfo, payersCodesInfo } = useSelector(
    (state: any) => state.orderSlice
  );

  const { data: accountData } = useGetAccountByIdQuery(accountId, {
    skip: !accountId,
  });

  const orderTypeField = watch("orderType");
  const secondaryInsuranceActivatedField = watch("secondaryInsuranceActivated");
  const payerField = watch("payer");
  const { loadingPayersName } = usePayerInformation(accountId);

  const mainProvider = useMemo(() => {
    return (
      accountData?.data?.providers.find(
        (aMainProvider) => !!aMainProvider.mainFacility
      ) || {}
    );
  }, [accountData]);

  const handleAddSecondaryInsurance = () => {
    setValue("secondaryInsuranceActivated", true);
  };

  const handleRemoveSecondaryInsurance = () => {
    setValue("secondaryInsuranceActivated", false);
    setValue("payerName2", null);
    setValue("payer2", null);
    setValue("memberId2", null);
    setValue("practiceType2", null);
    setValue("deductible2", null);
    setValue("outOfPocketMax2", null);
    setValue("flatCopay2", null);
    setValue("coinsurancePercent2", null);
    setValue("benefitDetails2", null);
    setValue("secondaryBenefitsUpdatedAt", null);
  };

  useEffect(() => {
    const payer = payersNameInfo.find(
      (payer: any) => payer.value?.payerId === payerField?.payerId
    );
    if (payer?.name === "Self-Pay Rate") {
      setShowSecondaryInsuranceButton(false);
      handleRemoveSecondaryInsurance();
    } else {
      setShowSecondaryInsuranceButton(true);
    }
  }, [payerField]);

  return (
    <StyledCard title="Patient Benefits" ordertypefield={orderTypeField}>
      <PrimaryInsuranceSection
        loadingPayersName={loadingPayersName}
        payersNameInfo={payersNameInfo}
        payersCodesInfo={payersCodesInfo}
        redo={redo}
        draft={draft}
        copy={copy}
        isEligibilityEnabledForMainProvider={!!mainProvider?.eligibilityEnabled}
        accountId={accountId}
        mainProvider={mainProvider}
        costApplicationRules={costApplicationRules}
        account={account}
        draftOrder={draftOrder}
        orderToRedo={orderToRedo}
        orderToCopy={orderToCopy}
      />
      {!secondaryInsuranceActivatedField && showSecondaryInsuranceButton && (
        <Button
          label="Add Secondary Insurance"
          onClick={handleAddSecondaryInsurance}
          className="p-button-outlined mt-4 ml-4"
        />
      )}
      {secondaryInsuranceActivatedField && (
        <SecondaryInsuranceSection
          loadingPayersName={loadingPayersName}
          payersNameInfo={payersNameInfo}
          payersCodesInfo={payersCodesInfo}
          redo={redo}
          draft={draft}
          copy={copy}
          isEligibilityEnabledForMainProvider={
            !!mainProvider?.eligibilityEnabled
          }
          accountId={accountId}
          mainProvider={mainProvider}
          costApplicationRules={secondaryCostApplicationRules}
          account={account}
          draftOrder={draftOrder}
          orderToRedo={orderToRedo}
          orderToCopy={orderToCopy}
          handleRemoveSecondaryInsurance={handleRemoveSecondaryInsurance}
        />
      )}
    </StyledCard>
  );
}
